/**
 * Root module paths that do NOT require a tenant to exist.
 *
 * Everything NOT listed here is treated as a business module: it is gated by `tenantGuard`
 * (redirected when the user has zero tenants) and dimmed in the module-rail. Keeping the
 * exempt set explicit and small means any NEW business module is protected by default
 * (fail-safe) — you only touch this list when adding another infra/admin area.
 *
 * Single source of truth shared by `tenantGuard` (route gating) and `ModuleRailComponent`
 * (item dimming) so the two can never disagree about what "needs a tenant" means.
 */
export const TENANT_EXEMPT_ROOT_PATHS: ReadonlySet<string> = new Set<string>([
  'administration', // hosts tenant create + setup-wizard — the zero-tenant bootstrap path
  'security', // users, roles, permissions, sessions, SSO — managed without/before a tenant
  'profile',
  'platform',
  'governance',
  'notifications',
  'knowledge',
  'component-library',
  'test-center',
]);

/** First non-empty URL segment, e.g. `/warehouse/goods-receipt?x=1` → `warehouse`. */
export function rootSegmentOf(url: string): string {
  return url.split(/[?#]/)[0].split('/').filter(Boolean)[0] ?? '';
}

/** True when the given app URL points at a module that requires a tenant to be present. */
export function pathRequiresTenant(url: string): boolean {
  return !TENANT_EXEMPT_ROOT_PATHS.has(rootSegmentOf(url));
}
